import React from 'react';
import YoutubePlayer from '../components/YoutubePlayer';
import { CONTACT_EMAIL } from '../constants/contact';
import { heroFeaturesMobile, heroFeaturesDesktop, YOUTUBE_VIDEO_ID } from '../constants/programData';

const HeroSection = ({ onDonateClick }) => {
  return (
    <div className="hero" id="hero">
      <div className="hero-content flex items-center justify-center">
        <div className="container mx-auto px-4 sm:px-6 pt-36 sm:pt-40 pb-6 sm:pb-16 relative z-10">
          <div className="bg-transparent border-0 shadow-none">

            {/* Mobile Layout */}
            <div className="lg:hidden">
              <div className="text-center mb-6">
                <p className="text-green-300/90 text-xs font-semibold tracking-widest uppercase mb-3 drop-shadow-md">
                  Est. 1992 · Harford County, MD
                </p>
                <h1 className="text-2xl font-bold text-white drop-shadow-lg leading-tight font-display mb-3"
                    style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
                  Therapeutic Riding at Normandy Farm
                </h1>
                <p className="text-lg font-semibold text-white mb-6 leading-tight drop-shadow-2xl font-display italic"
                    style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
                  Empowering lives through the{' '}
                  <span className="text-green-300">healing power of horses.</span>
                </p>
              </div>

              {/* Video - Mobile */}
              <div className="flex justify-center mb-6">
                <div className="relative max-w-lg w-full">
                  <div className="relative bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl overflow-hidden border border-white/20 p-3">
                    <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                      <div className="absolute inset-0">
                        <YoutubePlayer videoId={YOUTUBE_VIDEO_ID} className="w-full h-full object-cover rounded-xl" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description + CTAs - Mobile */}
              <div className="text-center">
                <div className="bg-black/20 backdrop-blur-sm rounded-2xl p-4 mb-6 border border-white/10">
                  <p className="text-base text-white mb-4 leading-relaxed drop-shadow-lg">
                    Since 1992, Therapeutic Riding at Normandy Farm has brought hope and healing to Harford County,
                    Maryland, transforming lives by building restorative bonds with horses.
                  </p>
                  <div className="space-y-3">
                    {heroFeaturesMobile.map((feature) => (
                      <div key={feature} className="flex items-center text-white">
                        <svg className="w-5 h-5 text-green-300 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm drop-shadow-md">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col gap-3 items-center w-full">
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-full text-base transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl min-h-[48px] flex items-center justify-center"
                  >
                    Contact Us
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a2 2 0 002.82 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </a>
                  <button
                    onClick={onDonateClick}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 px-6 rounded-full text-base transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center min-h-[48px]"
                  >
                    Donate Now
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden lg:grid grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="text-left">
                <p className="text-green-300/90 text-sm font-semibold tracking-widest uppercase mb-4 drop-shadow-md">
                  Est. 1992 · Harford County, MD
                </p>
                <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-white drop-shadow-lg leading-tight font-display mb-4"
                    style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
                  Therapeutic Riding at Normandy Farm
                </h1>
                <p className="text-2xl lg:text-3xl xl:text-4xl font-semibold text-white mb-6 leading-tight font-display italic"
                    style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
                  Empowering lives through the{' '}
                  <span className="text-green-300">healing power of horses.</span>
                </p>

                <div className="bg-black/20 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-white/10">
                  <p className="text-lg lg:text-xl text-white mb-5 leading-relaxed drop-shadow-lg">
                    Since 1992, Therapeutic Riding at Normandy Farm has brought hope and healing to Harford County,
                    Maryland, transforming lives by building restorative bonds with horses.
                  </p>
                  <div className="space-y-3">
                    {heroFeaturesDesktop.map((feature) => (
                      <div key={feature} className="flex items-center text-white">
                        <svg className="w-6 h-6 text-green-300 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-base lg:text-lg drop-shadow-md">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-row gap-4 items-center">
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="bg-green-600 hover:bg-green-700 text-white font-semibold py-4 px-8 rounded-full text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl min-h-[48px] flex items-center"
                  >
                    Contact Us
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a2 2 0 002.82 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </a>
                  <button
                    onClick={onDonateClick}
                    className="bg-amber-600 hover:bg-amber-700 text-white font-semibold py-4 px-8 rounded-full text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center min-h-[48px]"
                  >
                    Donate Now
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Video - Desktop */}
              <div className="flex justify-end">
                <div className="relative max-w-2xl w-full">
                  <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl shadow-2xl overflow-hidden border border-white/20 p-6">
                    <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                      <div className="absolute inset-0">
                        <YoutubePlayer videoId={YOUTUBE_VIDEO_ID} className="w-full h-full object-cover rounded-2xl" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
